import { HttpParams } from "@angular/common/http";

export interface ApiFilter {
  field: string;
  operator: FilterOperator;
  value?: any;
}

export type FilterOperator =
  | 'eq'
  | 'ne'
  | 'gt'
  | 'lt'
  | 'gte'
  | 'lte'
  | 'starts'
  | 'ends'
  | 'cont'
  | 'excl'
  | 'in'
  | 'notin'
  | 'isnull'
  | 'notnull'
  | 'between';

export interface ApiSort {
  field: string;
  order: 'ASC' | 'DESC';
}

export interface ApiPopulate {
  field: string;
  select?: string[];
  sort?: string;
  filters?: Record<string, any>;
}

export interface ApiRequestOptions {
  filter?: ApiFilter[];
  sort?: ApiSort[];
  populate?: string[] | ApiPopulate[];
  skip?: number;
  limit?: number;
  fields?: string[];
  logicalOperator?: Record<string, 'and' | 'or'>;
}

export interface ApiResponse<T> {
  results: T[];
  total: number;
  page: number;
  lastPage?: number;
}


/**
 * Construye los HttpParams basados en CrudRequestOptions.
 * Esta es una implementación básica. Una librería como 'qs' podría manejar casos más complejos.
 */
export function buildApiHttpParams(options?: ApiRequestOptions): HttpParams {
  let params = new HttpParams();
  if (!options) {
    return params;
  }

  // Procesar filtros - formato field[operator]=value
  if (options.filter?.length) {
    options.filter.forEach(f => {
      params = params.set(`${f.field}[${f.operator}]`, f.value?.toString() || 'true');
      
      // Si hay un operador lógico específico para este campo
      if (options.logicalOperator && options.logicalOperator[f.field]) {
        params = params.set(`${f.field}_op`, options.logicalOperator[f.field]);
      }
    });
  }

  // Procesar ordenamiento - formato sort=field o sort=-field para descendente
  if (options.sort?.length) {
    const sortParams = options.sort.map(s => {
      return s.order === 'DESC' ? `-${s.field}` : s.field;
    });
    params = params.set('sort', sortParams.join(','));
  }

  // Procesamiento básico de población
  if (options.populate) {
    if (Array.isArray(options.populate) && typeof options.populate[0] === 'string') {
      // Si es un array de strings, simplemente los unimos
      params = params.set('populate', options.populate.join(','));
    } else if (Array.isArray(options.populate)) {
      // Si son objetos de población complejos
      (options.populate as ApiPopulate[]).forEach(p => {
        params = params.set('populate', p.field);
        
        if (p.select?.length) {
          params = params.set(`populate.${p.field}.select`, p.select.join(','));
        }
        
        if (p.sort) {
          params = params.set(`populate.${p.field}.sort`, p.sort);
        }
        
        // Procesar filtros para la población
        if (p.filters) {
          Object.entries(p.filters).forEach(([key, value]) => {
            params = params.set(`populate.${p.field}.${key}`, value.toString());
          });
        }
      });
    }
  }

  // Paginación
  if (options.skip !== undefined) {
    params = params.set('skip', options.skip.toString());
  }

  if (options.limit !== undefined) {
    params = params.set('limit', options.limit.toString());
  }

  // Selección de campos
  if (options.fields?.length) {
    params = params.set('select', options.fields.join(','));
  }

  return params;
}